import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { CustomClass } from './config';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

export interface UploadResponse {
  imageUrl: string;
}

@Injectable()
export class AngularEditorService {

  savedSelection: Range | null;
  selectedText: string;
  uploadUrl: string;
  uploadWithCredentials: boolean;

  constructor(private snackBar: MatSnackBar, private translate: TranslateService,
    private http: HttpClient,
    @Inject(DOCUMENT) private doc: any
  ) { }

  /**
   * Executed command from editor header buttons exclude toggleEditorMode
   * @param command string from triggerCommand
   * @param value
   */
  executeCommand(command: string, value?: string) {
    // Ensure selection is restored before executing a command invoked from the toolbar
    this.restoreSelection();
    // Avoid changing existing formatted text: if a range is selected, collapse to caret
    this.collapseSelectionToCaret();
    const commands = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'pre'];
    if (commands.includes(command)) {
      this.doc.execCommand('formatBlock', false, command);
      return;
    }
    // Proper toggle behavior and mutual exclusion for sub/superscript
    if (command === 'subscript') {
      this.toggleInlineTag('SUB', 'SUP');
      return;
    }
    if (command === 'superscript') {
      this.toggleInlineTag('SUP', 'SUB');
      return;
    }
    this.doc.execCommand(command, false, value);
  }
  /**
   * Toggle an inline tag (SUB/SUP) at the caret/selection.
   * Ensures mutual exclusivity with the opposite tag and avoids reformatting existing text.
   */
  private toggleInlineTag(enableTag: 'SUB' | 'SUP', oppositeTag: 'SUB' | 'SUP'): void {
    const sel = this.doc.getSelection ? this.doc.getSelection() : null;
    const enableCmd = enableTag === 'SUB' ? 'subscript' : 'superscript';
    const oppositeCmd = oppositeTag === 'SUB' ? 'subscript' : 'superscript';

    if (sel && sel.rangeCount > 0 && sel.isCollapsed) {
      // Collapsed caret: use browser command to control typing state
      if (this.isCaretInsideTag(enableTag)) {
        // Toggle off by exiting the current tag (preserve existing tags/content)
        this.exitClosestTag(enableTag);
        // Ensure browser typing state is OFF for this tag
        if (this.doc.queryCommandState(enableCmd)) {
          this.doc.execCommand(enableCmd, false);
        }
        return;
      }
      if (this.isCaretInsideTag(oppositeTag)) {
        // Exit opposite tag first so existing content stays as-is
        this.exitClosestTag(oppositeTag);
        // Ensure browser typing state is OFF for the opposite tag
        if (this.doc.queryCommandState(oppositeCmd)) {
          this.doc.execCommand(oppositeCmd, false);
        }
      }
      // Enable desired typing mode
      this.doc.execCommand(enableCmd, false);
      return;
    }

    // Selection exists: wrap/unwrap explicitly
    if (this.isCaretInsideTag(enableTag)) {
      // Selection exists: unwrap only the selected part of the tag
      this.unwrapClosestTag(enableTag);
      return;
    }
    if (this.isCaretInsideTag(oppositeTag)) {
      this.unwrapClosestTag(oppositeTag);
    }
    this.applyInlineTag(enableTag);
  }
  /**
   * Wrap current selection (or insert at caret) with a given inline tag and place caret inside.
   */
  private applyInlineTag(tagName: 'SUB' | 'SUP'): void {
    try {
      const sel = this.doc.getSelection ? this.doc.getSelection() : null;
      if (!sel || sel.rangeCount === 0) {
        return;
      }
      const range = sel.getRangeAt(0);
      const el = this.doc.createElement(tagName.toLowerCase());
      if (range.collapsed) {
        // Insert empty tag and move caret inside it
        el.appendChild(this.doc.createTextNode(''));
        range.insertNode(el);
        // Move caret inside the new element
        sel.removeAllRanges();
        const newRange = this.doc.createRange();
        newRange.setStart(el.firstChild, 0);
        newRange.collapse(true);
        sel.addRange(newRange);
      } else {
        // Wrap selection contents
        const contents = range.extractContents();
        el.appendChild(contents);
        range.insertNode(el);
        // Place caret at end of the new element
        sel.removeAllRanges();
        const newRange = this.doc.createRange();
        newRange.selectNodeContents(el);
        newRange.collapse(false);
        sel.addRange(newRange);
      }
    } catch {
      // ignore
    }
  }
  /**
   * Collapse the current selection to the caret to avoid reformatting existing content
   */
  private collapseSelectionToCaret(): void {
    try {
      const sel = this.doc.getSelection ? this.doc.getSelection() : null;
      if (!sel || sel.rangeCount === 0) {
        return;
      }
      if (!sel.isCollapsed) {
        const range = sel.getRangeAt(0);
        range.collapse(false); // collapse to end
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } catch {
      // ignore
    }
  }
  /**
   * Unwrap the closest ancestor tag (e.g., SUB/SUP) around the caret/selection
   */
  private unwrapClosestTag(tagName: string): void {
    try {
      const sel = this.doc.getSelection ? this.doc.getSelection() : null;
      if (!sel || sel.rangeCount === 0) {
        return;
      }
      let node: any = sel.focusNode;
      if (!node) {
        return;
      }
      if (node.nodeType === 3) {
        node = node.parentNode;
      }
      while (node && node.nodeName && node.nodeName !== 'BODY') {
        if (node.nodeType === 1 && node.nodeName.toUpperCase() === tagName.toUpperCase()) {
          this.replaceWithOwnChildren(node);
          break;
        }
        node = node.parentNode;
      }
    } catch {
      // ignore
    }
  }
  /**
   * Move the caret outside (after) the closest ancestor tag without removing it
   */
  private exitClosestTag(tagName: string): void {
    try {
      const sel = this.doc.getSelection ? this.doc.getSelection() : null;
      if (!sel || sel.rangeCount === 0) {
        return;
      }
      let node: any = sel.focusNode;
      if (!node) {
        return;
      }
      if (node.nodeType === 3) {
        node = node.parentNode;
      }
      while (node && node.nodeName && node.nodeName !== 'BODY') {
        if (node.nodeType === 1 && node.nodeName.toUpperCase() === tagName.toUpperCase()) {
          const range = this.doc.createRange();
          range.setStartAfter(node);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          return;
        }
        node = node.parentNode;
      }
    } catch {
      // ignore
    }
  }
  /**
   * Checks if the current caret/selection is inside a given tag (e.g., SUB/SUP)
   */
  private isCaretInsideTag(tagName: string): boolean {
    try {
      const sel = this.doc.getSelection ? this.doc.getSelection() : null;
      if (!sel || sel.rangeCount === 0) {
        return false;
      }
      let node: any = sel.focusNode;
      if (!node) {
        return false;
      }
      if (node.nodeType === 3) {
        node = node.parentNode;
      }
      while (node && node.nodeName && node.nodeName !== 'BODY') {
        if (node.nodeName.toUpperCase() === tagName.toUpperCase()) {
          return true;
        }
        node = node.parentNode;
      }
      return false;
    } catch {
      return false;
    }
  }
  /**
   * Create URL link
   * @param url string from UI prompt
   */
  createLink(url: string) {
    if (!url.includes('http')) {
      this.doc.execCommand('createlink', false, url);
    } else {
      const newUrl = '<a href="' + url + '" target="_blank">' + this.selectedText + '</a>';
      this.insertHtml(newUrl);
    }
  }

  /**
   * insert color either font or background
   *
   * @param color color to be inserted
   * @param where where the color has to be inserted either text/background
   */
  insertColor(color: string, where: string): void {
    const restored = this.restoreSelection();
    if (restored) {
      if (where === 'textColor') {
        this.doc.execCommand('foreColor', false, color);
      } else {
        this.doc.execCommand('hiliteColor', false, color);
      }
    }
  }

  /**
   * Set font name
   * @param fontName string
   */
  setFontName(fontName: string) {
    if (fontName !== '') {
      this.doc.execCommand('fontName', false, fontName);
    } else {
      this.doc.execCommand('fontName', false, 'inherit');
    }
  }
  /**
   * Set font size
   * @param fontName string
   */
  setFontNameCss(fontName: string) {
    let newTag = this.selectedText;
    if (fontName) {
      const tagName = 'span';
      newTag = '<' + tagName + ' style="font-family:' + fontName + '">' + this.selectedText + '</' + tagName + '>';
    }
    this.insertHtml(newTag);
  }

  /**
   * Set font size
   * @param fontSize string
   */
  setFontSize(fontSize: string) {
    this.doc.execCommand('fontSize', false, fontSize);
  }
  /**
   * Set font size
   * @param fontSize string
   */
  setFontSizeCss(fontSize: string) {
    let newTag = this.selectedText;
    if (fontSize) {
      const tagName = 'span';
      newTag = '<' + tagName + ' style="font-size:' + fontSize + '">' + this.selectedText + '</' + tagName + '>';
    }
    this.insertHtml(newTag);
  }

  /**
   * Create raw HTML
   * @param html HTML string
   */
  insertHtml(html: string): void {

    const isHTMLInserted = this.doc.execCommand('insertHTML', false, html);

    if (!isHTMLInserted) {
      //throw new Error('Unable to perform the operation');
      console.log('Unable to perform the operation');
    }
  }

  /**
   * save selection when the editor is focussed out
   */
  public saveSelection = (): void => {
    if (this.doc.getSelection) {
      const sel = this.doc.getSelection();
      if (sel.getRangeAt && sel.rangeCount) {
        this.savedSelection = sel.getRangeAt(0);
        this.selectedText = sel.toString();
      }
    } else if (this.doc.getSelection && this.doc.createRange) {
      this.savedSelection = document.createRange();
    } else {
      this.savedSelection = null;
    }
  }

  /**
   * restore selection when the editor is focused in
   *
   * saved selection when the editor is focused out
   */
  restoreSelection(): boolean {
    if (this.savedSelection) {
      if (this.doc.getSelection) {
        const sel = this.doc.getSelection();
        sel.removeAllRanges();
        sel.addRange(this.savedSelection);
        return true;
      } else if (this.doc.getSelection /*&& this.savedSelection.select*/) {
        // this.savedSelection.select();
        return true;
      }
    } else {
      return false;
    }
  }

  /**
   * setTimeout used for execute 'saveSelection' method in next event loop iteration
   */
  public executeInNextQueueIteration(callbackFn: (...args: any[]) => any, timeout = 1e2): void {
    setTimeout(callbackFn, timeout);
  }

  /** check any selection is made or not */
  private checkSelection(): any {

    const selectedText = this.savedSelection.toString();

    if (selectedText.length === 0) {
      // throw new Error('No Selection Made');
      console.log('No Selection Made');
    }
    return true;
  }

  /**
   * Upload file to uploadUrl
   * @param file The file
   */
  uploadImage(file: File): Observable<HttpEvent<UploadResponse>> {

    const uploadData: FormData = new FormData();

    uploadData.append('file', file, file.name);

    return this.http.post<UploadResponse>(this.uploadUrl, uploadData, {
      reportProgress: true,
      observe: 'events',
      withCredentials: this.uploadWithCredentials,
    });
  }

  /**
   * Insert image with Url
   * @param imageObj The imageObj.
   */
  insertImageObject(imageObj: { imageUrl: '', width: '100%', height: 'auto', targetUrl: '', target: '_blank' }) {
    let width = imageObj.width;
    let height = imageObj.height;
    let thumbnail = '';
    if (imageObj.targetUrl === '') {
      thumbnail = `<img src="${imageObj.imageUrl}" style="width:${width};height:${height};"/>`;
    } else {
      thumbnail = `<a href="${imageObj.targetUrl}" target="${imageObj.target}"><img src="${imageObj.imageUrl}" style="width:${width};height:${height};"/></a>`
    }
    this.insertHtml(thumbnail);
  }
  /**
   * Insert image with Url
   * @param imageUrl The imageUrl.
   */
  insertImage(imageUrl) {
    this.doc.execCommand('insertImage', false, imageUrl);
  }

  setDefaultParagraphSeparator(separator: string) {
    this.doc.execCommand('defaultParagraphSeparator', false, separator);
  }

  createCustomClass(customClass: CustomClass) {
    let newTag = this.selectedText;
    if (customClass) {
      const tagName = customClass.tag ? customClass.tag : 'span';
      newTag = '<' + tagName + ' class="' + customClass.class + '">' + this.selectedText + '</' + tagName + '>';
    }
    this.insertHtml(newTag);
  }
  public showNotification(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 6000,
    });
  }
  insertVideoOld(videoUrl: string) {
    if (videoUrl.match('www.youtube.com')) {
      this.insertYouTubeVideoTag(videoUrl);
    }
    if (videoUrl.match('vimeo.com')) {
      this.insertVimeoVideoTag(videoUrl);
    }
  }
  public insertVideo(sUrl: string) {
    // video url patterns(youtube, instagram, vimeo, dailymotion, youku)
    var ytRegExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    var ytMatch = sUrl.match(ytRegExp);

    var igRegExp = /\/\/instagram.com\/p\/(.[a-zA-Z0-9]*)/;
    var igMatch = sUrl.match(igRegExp);

    var vRegExp = /\/\/vine.co\/v\/(.[a-zA-Z0-9]*)/;
    var vMatch = sUrl.match(vRegExp);

    var vimRegExp = /\/\/(player.)?vimeo.com\/([a-z]*\/)*([0-9]{6,11})[?]?.*/;
    var vimMatch = sUrl.match(vimRegExp);

    var dmRegExp = /.+dailymotion.com\/(video|hub)\/([^_]+)[^#]*(#video=([^_&]+))?/;
    var dmMatch = sUrl.match(dmRegExp);

    var youkuRegExp = /\/\/v\.youku\.com\/v_show\/id_(\w+)\.html/;
    var youkuMatch = sUrl.match(youkuRegExp);

    var $video;

    if (ytMatch && ytMatch[2].length === 11) {
      var youtubeId = ytMatch[2];
      $video = "<iframe src='https://www.youtube.com/embed/" + youtubeId + "?playsinline=1' width='320' height='180' webkitallowfullscreen mozallowfullscreen allowfullscreen msallowfullscreen ></iframe>";

    } else if (igMatch && igMatch[0].length) {
      $video = "<iframe src='" + igMatch[0] + "/embed/' width='320' height='180' scrolling='no' allowtransparency='true'></iframe>";

    } else if (vMatch && vMatch[0].length) {
      $video = "<iframe src='" + vMatch[0] + "/embed/simple' width='320' height='180' class='vine-embed'></iframe>";

    } else if (vimMatch && vimMatch[3].length) {
      $video = "<iframe webkitallowfullscreen mozallowfullscreen allowfullscreen  src='https://player.vimeo.com/video/" + vimMatch[3] + "?api=1' width='320' height='180' class='ck-vimeo-player'></iframe>";

    } else if (dmMatch && dmMatch[2].length) {
      $video = "<iframe src='https://www.dailymotion.com/embed/video/" + dmMatch[2] + "' width='320' height='180'></iframe>";

    } else if (youkuMatch && youkuMatch[1].length) {
      $video = "<iframe webkitallowfullscreen mozallowfullscreen allowfullscreen height='180' width='320' src='https://player.youku.com/embed/" + youkuMatch[1] + "'></iframe>";
    }
    this.insertHtml($video);
  };
  private insertYouTubeVideoTag(videoUrl: string): void {
    const id = videoUrl.split('v=')[1];
    const imageUrl = `https://img.youtube.com/vi/${id}/0.jpg`;
    const thumbnail = `
      <div style='position: relative'>
        <a href='${videoUrl}' target='_blank'>
          <img src="${imageUrl}" alt="click to watch"/>
          <img style='position: absolute; left:200px; top:140px'
          src="https://img.icons8.com/color/96/000000/youtube-play.png"/>
        </a>
      </div>`;
    this.insertHtml(thumbnail);
  }

  private insertVimeoVideoTag(videoUrl: string): void {
    const sub = this.http.get<any>(`https://vimeo.com/api/oembed.json?url=${videoUrl}`).subscribe(data => {
      const imageUrl = data.thumbnail_url_with_play_button;
      const thumbnail = `<div>
        <a href='${videoUrl}' target='_blank'>
          <img src="${imageUrl}" alt="${data.title}"/>
        </a>
      </div>`;
      sub.unsubscribe();
    });
  }

  nextNode(node) {
    if (node.hasChildNodes()) {
      return node.firstChild;
    } else {
      while (node && !node.nextSibling) {
        node = node.parentNode;
      }
      if (!node) {
        return null;
      }
      return node.nextSibling;
    }
  }

  getRangeSelectedNodes(range, includePartiallySelectedContainers) {
    let node = range.startContainer;
    const endNode = range.endContainer;
    let rangeNodes = [];

    // Special case for a range that is contained within a single node
    if (node === endNode) {
      rangeNodes = [node];
    } else {
      // Iterate nodes until we hit the end container
      while (node && node !== endNode) {
        rangeNodes.push(node = this.nextNode(node));
      }

      // Add partially selected nodes at the start of the range
      node = range.startContainer;
      while (node && node !== range.commonAncestorContainer) {
        rangeNodes.unshift(node);
        node = node.parentNode;
      }
    }

    // Add ancestors of the range container, if required
    if (includePartiallySelectedContainers) {
      node = range.commonAncestorContainer;
      while (node) {
        rangeNodes.push(node);
        node = node.parentNode;
      }
    }

    return rangeNodes;
  }

  getSelectedNodes() {
    const nodes = [];
    if (this.doc.getSelection) {
      const sel = this.doc.getSelection();
      for (let i = 0, len = sel.rangeCount; i < len; ++i) {
        nodes.push.apply(nodes, this.getRangeSelectedNodes(sel.getRangeAt(i), true));
      }
    }
    return nodes;
  }

  replaceWithOwnChildren(el) {
    const parent = el.parentNode;
    while (el.hasChildNodes()) {
      parent.insertBefore(el.firstChild, el);
    }
    parent.removeChild(el);
  }

  removeSelectedElements(tagNames) {
    const tagNamesArray = tagNames.toLowerCase().split(',');
    this.getSelectedNodes().forEach((node) => {
      if (node.nodeType === 1 &&
        tagNamesArray.indexOf(node.tagName.toLowerCase()) > -1) {
        // Remove the node and replace it with its children
        this.replaceWithOwnChildren(node);
      }
    });
  }
}
